// Shared hero gallery (embla-based) used by PlaceProfile / FriendProfile / MyProfile.
//
// Three slide types are supported:
//   • photo: `{full?, medium?, thumbnail?}` (legacy shape used by FriendProfile)
//   • image: `{kind: "image", src}` (normalized; used by PlaceProfile)
//   • youtube: `{kind: "youtube", youtube_id, poster}` (used by PlaceProfile)
//
// Empty state renders either an initials placeholder (FriendProfile) or a
// custom slot via the optional `renderEmpty` prop (PlaceProfile category icon).
import { useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";

function normalizeSlides(photos) {
  return (photos || [])
    .map((p) => {
      if (!p) return null;
      if (p.kind === "youtube" || (p.media_type === "youtube" && p.youtube_id)) {
        return { kind: "youtube", youtube_id: p.youtube_id, poster: p.poster || p.poster_url || `https://i.ytimg.com/vi/${p.youtube_id}/hqdefault.jpg` };
      }
      if (p.kind === "image") return { kind: "image", src: p.src };
      const src = p.full || p.medium || p.thumbnail || p.url;
      if (!src) return null;
      return { kind: "image", src };
    })
    .filter(Boolean);
}

function ImageSlide({ src, eager, testId }) {
  return (
    <img
      src={src}
      alt=""
      loading={eager ? "eager" : "lazy"}
      className="w-full h-full object-cover"
      data-testid={testId}
    />
  );
}

function YouTubeSlide({ youtube_id, poster, active, idx, testIdPrefix }) {
  // Render poster + iframe overlay only when active (saves bandwidth)
  if (!active) {
    return (
      <div className="relative w-full h-full">
        <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid={`${testIdPrefix}-yt-poster-${idx}`} />
      </div>
    );
  }
  const src = `https://www.youtube.com/embed/${youtube_id}?autoplay=1&mute=1&controls=0&loop=1&playlist=${youtube_id}&modestbranding=1&playsinline=1&rel=0`;
  return (
    <div className="relative w-full h-full">
      <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid={`${testIdPrefix}-yt-poster-${idx}`} />
      <div className="absolute inset-0 pointer-events-none" data-testid={`${testIdPrefix}-yt-${idx}`}>
        <iframe
          src={src}
          title=""
          allow="autoplay; encrypted-media"
          className="w-full h-full"
          style={{ border: 0 }}
        />
      </div>
    </div>
  );
}

export default function MediaCarousel({
  photos = [],
  initials = "?",
  testIdPrefix = "media-hero",
  height,
  renderEmpty,
  showDots = true,
  showCounter = true,
  children, // overlay slot (back/save buttons etc.)
}) {
  const [emblaRef, embla] = useEmblaCarousel({ loop: false, dragFree: false });
  const [active, setActive] = useState(0);
  const slides = normalizeSlides(photos);

  useEffect(() => {
    if (!embla) return;
    const onSelect = () => setActive(embla.selectedScrollSnap());
    embla.on("select", onSelect);
    onSelect();
    return () => embla.off("select", onSelect);
  }, [embla]);

  // Empty state
  if (!slides.length) {
    return (
      <div
        data-testid={`${testIdPrefix}-placeholder`}
        className="relative w-full"
        style={{ height: height ?? 320, background: "#171717", borderBottom: "1px solid rgba(232,184,75,0.15)" }}
      >
        {renderEmpty ? (
          renderEmpty()
        ) : (
          <div className="w-full h-full grid place-items-center">
            <span
              style={{ fontFamily: "'Playfair Display', serif", fontSize: 64, color: "#E8B84B", letterSpacing: 4 }}
              aria-hidden
            >
              {initials}
            </span>
          </div>
        )}
        {children}
      </div>
    );
  }

  // Single slide
  if (slides.length === 1) {
    const s = slides[0];
    return (
      <div data-testid={`${testIdPrefix}-single`} className="relative w-full" style={{ height: height ?? 320, background: "#171717" }}>
        {s.kind === "youtube"
          ? <YouTubeSlide youtube_id={s.youtube_id} poster={s.poster} active idx={0} testIdPrefix={testIdPrefix} />
          : <ImageSlide src={s.src} eager testId={`${testIdPrefix}-img-0`} />}
        {children}
      </div>
    );
  }

  // Multiple slides — embla
  return (
    <div
      data-testid={`${testIdPrefix}-gallery`}
      className="relative w-full"
      style={{ height: height ?? 360, background: "#171717" }}
    >
      <div ref={emblaRef} className="overflow-hidden h-full" data-testid={`${testIdPrefix}-viewport`}>
        <div className="flex h-full">
          {slides.map((s, i) => (
            <div key={i} className="flex-[0_0_100%] min-w-0 h-full" data-testid={`${testIdPrefix}-slide-${i}`}>
              {Math.abs(i - active) <= 1 ? (
                s.kind === "youtube"
                  ? <YouTubeSlide youtube_id={s.youtube_id} poster={s.poster} active={i === active} idx={i} testIdPrefix={testIdPrefix} />
                  : <ImageSlide src={s.src} eager={i === 0} testId={`${testIdPrefix}-img-${i}`} />
              ) : (
                <div className="w-full h-full bg-[#0a0a0a]" />
              )}
            </div>
          ))}
        </div>
      </div>
      {showDots && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10" data-testid={`${testIdPrefix}-dots`}>
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => embla?.scrollTo(i)}
              aria-label={`Slide ${i + 1}`}
              data-testid={`${testIdPrefix}-dot-${i}`}
              className="transition-all"
              style={{
                width: i === active ? 18 : 6,
                height: 6,
                borderRadius: 3,
                background: i === active ? "#E8B84B" : "rgba(255,255,255,0.7)",
                border: "none",
                cursor: "pointer",
              }}
            />
          ))}
        </div>
      )}
      {showCounter && (
        <div
          data-testid={`${testIdPrefix}-counter`}
          className="absolute top-3 right-3 px-2.5 py-1 z-10"
          style={{
            background: "rgba(0,0,0,0.55)",
            color: "#F5F1E8",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 12,
            borderRadius: 99,
            backdropFilter: "blur(6px)",
          }}
        >
          {active + 1} / {slides.length}
        </div>
      )}
      {children}
    </div>
  );
}
